// src/components/home/dashboard/Dashboard.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Building2,
  FileText,
  Calendar,
  ChevronRight,
  FileEdit,
  Rocket,
} from "lucide-react";
import "./dashboard.css";
import useDashboard from "../../../hooks/useDashboard";
import BrandCard from "../../uiComponents/BrandCard";
import Shimmer from "../../shimmer/Shimmer";
import Modal from "../../uiComponents/Modal";
import AddBrand from "../add-brand/AddBrand";
import ContentStatusListModal from "./ContentStatusListModal";
import { AppRoute } from "../../../types/routes";
import { BrandDetails } from "../../../types/add-brand-types";
import { ContentLog } from "../../../types/dashboard";
import DashboardSidebar from "./DashboardSidebar";
import useDashboardSidebar from "../../../hooks/use-dashboard-sidebar";
import usePaddleCheckout from "../../../hooks/use-paddle-checkout";
import useCurrentPlan from "../../../hooks/use-current-plan";
import useAutoProCheckout from "../../../hooks/use-auto-pro-checkout";
import useProUpgradeModal from "../../../hooks/use-pro-upgrade-modal";
import WeeklyWorkQueue from "../weekly-work-queue/WeeklyWorkQueue";

interface DashboardProps {
  onLogout: () => void;
}

const useAnimatedCount = (target: number, isLoading: boolean) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const safeTarget = Math.max(0, Math.round(target));
    if (safeTarget === 0 || typeof window === "undefined") {
      setValue(safeTarget);
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(safeTarget);
      return;
    }

    let frameId = 0;
    const duration = 700;
    const start = performance.now();

    setValue(0);

    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(safeTarget * easedProgress));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animate);
      }
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isLoading, target]);

  return value;
};

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [statusModal, setStatusModal] = useState<
    "SCHEDULED" | "DRAFT" | "PROCESSING" | "FAILED" | "PARTIAL_FAILURE" | null
  >(null);

  const {
    brandList,
    loadingBrandList,
    loadingContentLogs,
    contentStats,
    loadingStats,
    onViewDetailsClick,
    onDeleteClick,
    onDeleteCancel,
    onDeleteConfirm,
    deleteConfirmation,
    user,
    loadingUser,
    contentLogs,
    rawContentLogs,
    getIconForPlatform,
    handleLogout: localLogout,
    formatDate,
    replace,
    push,
  } = useDashboard();

  const contentGeneratedTotal =
    contentStats.scheduled +
    contentStats.draft +
    contentStats.published +
    contentStats.processing +
    contentStats.failed +
    contentStats.partial_failure;

  const totalBrands = brandList?.length ?? 0;

  const { openCheckout, isOpeningCheckout, isConfigured, pricesConfigured } =
    usePaddleCheckout();
  const {
    plan,
    loading: isPlanLoading,
    refresh: refreshPlanSummary,
    hasReachedBrandLimit,
  } = useCurrentPlan();
  const betaEnabled = Boolean(plan?.betaEnabled);
  const betaLabel = plan?.betaLabel ?? "Free beta";
  const isUpgradeDisabled =
    betaEnabled || !isConfigured || isPlanLoading || plan?.planTier === "agency";
  const { openUpgradeModal, upgradeModal } = useProUpgradeModal({
    onUpgradeCheckout: openCheckout,
    isUpgradeDisabled,
    isUpgradeLoading: isOpeningCheckout,
    currentPlanTier: plan?.planTier,
    pricesConfigured,
  });
  const handleOpenAddBrandModal = useCallback(() => {
    if (
      plan?.limits.brands != null &&
      (plan?.usage.brands ?? 0) >= plan.limits.brands
    ) {
      return;
    }
    setShowAddBrandModal(true);
  }, [plan?.limits.brands, plan?.usage.brands]);
  const { planName, usageMetrics, navItems, handleNavClick, proHighlights } =
    useDashboardSidebar({
      planSummary: plan,
    });
  const showUpgradeCard =
    !isPlanLoading && (betaEnabled || plan?.planTier !== "agency");
  const isAddBrandDisabled = isPlanLoading || hasReachedBrandLimit;
  const addBrandDisabledReason = hasReachedBrandLimit
    ? `${planName} brand limit reached. Upgrade to add more brands.`
    : undefined;
  const animatedTotalBrands = useAnimatedCount(totalBrands, loadingBrandList);
  const animatedContentGeneratedTotal = useAnimatedCount(
    contentGeneratedTotal,
    loadingStats,
  );
  const animatedScheduled = useAnimatedCount(
    contentStats.scheduled,
    loadingStats,
  );
  const animatedDraft = useAnimatedCount(contentStats.draft, loadingStats);
  const animatedProcessing = useAnimatedCount(
    contentStats.processing,
    loadingStats,
  );
  const animatedFailed = useAnimatedCount(
    contentStats.failed + contentStats.partial_failure,
    loadingStats,
  );

  useAutoProCheckout({
    betaEnabled,
    planTier: plan?.planTier,
    isPlanLoading,
    openCheckout,
  });

  const handleOpenLog = useCallback(
    (log: ContentLog) => {
      const route = AppRoute.BRAND_DETAILS.replace(":id", log.product_id);
      if ((log.status || "").toUpperCase() === "DRAFT") {
        push(route, { historyItem: log });
        return;
      }
      push(route, { analyticsItem: log });
    },
    [push],
  );

  const handleLogoutClick = useCallback(async () => {
    await localLogout();
    onLogout();
  }, [localLogout, onLogout]);

  const handleAddBrandSuccess = useCallback(async () => {
    await refreshPlanSummary();
    setShowAddBrandModal(false);
  }, [refreshPlanSummary]);

  const handleOpenBrandTab = useCallback(
    (
      brand: BrandDetails,
      activeTab: "drafts" | "history" | "calendar" | "weekly-plan",
    ) => {
      const route = AppRoute.BRAND_DETAILS.replace(":id", brand.id);
      push(route, { activeTab });
    },
    [push],
  );

  useEffect(() => {
    if (!user && !loadingUser && !loadingBrandList) {
      replace(AppRoute.LOGIN);
    }
  }, [user, loadingUser, loadingBrandList, replace]);

  const getList = useCallback(
    (str: string | string[] | undefined): string[] => {
      if (!str) return [];
      if (Array.isArray(str)) return str;
      return str
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    [],
  );

  const brandShimmer = useCallback(() => {
    return Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="ds-shimmer-card">
        <div className="ds-shimmer-header">
          <Shimmer width={48} height={48} borderRadius="50%" />
          <div className="ds-shimmer-text">
            <Shimmer width={120} height={20} />
            <Shimmer width={80} height={14} />
          </div>
        </div>
        <div className="ds-shimmer-section">
          <Shimmer width={80} height={12} style={{ marginBottom: "8px" }} />
          <div style={{ display: "flex", gap: "8px" }}>
            <Shimmer width={60} height={24} borderRadius={16} />
            <Shimmer width={70} height={24} borderRadius={16} />
          </div>
        </div>
        <div className="ds-shimmer-section">
          <Shimmer width={100} height={12} style={{ marginBottom: "8px" }} />
          <Shimmer width="90%" height={16} />
        </div>
      </div>
    ));
  }, []);

  const tableShimmer = useCallback(
    () => (
      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>
                <Shimmer width={60} />
              </th>
              <th>
                <Shimmer width={80} />
              </th>
              <th>
                <Shimmer width={60} />
              </th>
              <th>
                <Shimmer width={60} />
              </th>
              <th>
                <Shimmer width={40} />
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 3 }).map((_, i) => (
              <tr key={i}>
                <td>
                  <Shimmer width={120} />
                </td>
                <td>
                  <Shimmer width={180} />
                </td>
                <td>
                  <Shimmer width={80} />
                </td>
                <td>
                  <Shimmer width={100} />
                </td>
                <td>
                  <Shimmer width={40} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
    [],
  );

  return (
    <div
      className={`dashboard-page${
        isPlanLoading ? " dashboard-page--plan-loading" : ""
      }`}
    >
      <div className="dashboard-shell">
        <DashboardSidebar
          userName={user?.name}
          userEmail={user?.email}
          planName={planName}
          betaEnabled={betaEnabled}
          betaLabel={betaLabel}
          showUpgradeCard={showUpgradeCard}
          usageMetrics={usageMetrics}
          navItems={navItems}
          proHighlights={proHighlights}
          onNavClick={handleNavClick}
          onAddBrand={handleOpenAddBrandModal}
          onUpgrade={openUpgradeModal}
          onLogout={handleLogoutClick}
          isUpgradeDisabled={isUpgradeDisabled}
          isUpgradeLoading={isOpeningCheckout}
        />

        <main className="dashboard-content">
          <div className="ds-main">
            <section className="ds-welcome" id="dashboard-overview">
              <h1 className="ds-welcome-title">Welcome back</h1>
              <p className="ds-welcome-subtitle">
                Manage your brand identity and generate AI content in one place.
              </p>
              {betaEnabled && (
                <div className="ds-inline-notice" style={{ marginTop: "14px" }}>
                  {betaLabel} is live. All premium workflows are currently
                  unlocked and checkout is disabled while you explore the
                  product.
                </div>
              )}
            </section>

            <div className="ds-stats">
              <div className="ds-stat-card">
                <div className="ds-stat-icon ds-stat-icon-brands">
                  <Building2 size={20} />
                </div>
                <div className="ds-stat-content">
                  <span className="ds-stat-value">
                    {loadingBrandList ? "-" : animatedTotalBrands}
                  </span>
                  <span className="ds-stat-label">Total brands</span>
                </div>
              </div>
              <div className="ds-stat-card">
                <div className="ds-stat-icon ds-stat-icon-posts">
                  <FileText size={20} />
                </div>
                <div className="ds-stat-content">
                  <span className="ds-stat-value">
                    {loadingStats ? "-" : animatedContentGeneratedTotal}
                  </span>
                  <span className="ds-stat-label">Content history</span>
                </div>
              </div>
              <div
                className="ds-stat-card ds-stat-card-clickable"
                onClick={() => setStatusModal("SCHEDULED")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && setStatusModal("SCHEDULED")
                }
              >
                <div className="ds-stat-icon ds-stat-icon-calendar">
                  <Calendar size={20} />
                </div>
                <div className="ds-stat-content">
                  <span className="ds-stat-value">
                    {loadingStats ? "-" : animatedScheduled}
                  </span>
                  <span className="ds-stat-label">Scheduled</span>
                </div>
                <ChevronRight size={18} className="ds-stat-chevron" />
              </div>
              <div
                className="ds-stat-card ds-stat-card-clickable"
                onClick={() => setStatusModal("DRAFT")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setStatusModal("DRAFT")}
              >
                <div className="ds-stat-icon ds-stat-icon-draft">
                  <FileEdit size={20} />
                </div>
                <div className="ds-stat-content">
                  <span className="ds-stat-value">
                    {loadingStats ? "-" : animatedDraft}
                  </span>
                  <span className="ds-stat-label">In draft</span>
                </div>
                <ChevronRight size={18} className="ds-stat-chevron" />
              </div>
              <div
                className="ds-stat-card ds-stat-card-clickable"
                onClick={() => setStatusModal("PROCESSING")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && setStatusModal("PROCESSING")
                }
              >
                <div className="ds-stat-icon ds-stat-icon-calendar">
                  <Rocket size={20} />
                </div>
                <div className="ds-stat-content">
                  <span className="ds-stat-value">
                    {loadingStats ? "-" : animatedProcessing}
                  </span>
                  <span className="ds-stat-label">Processing</span>
                </div>
                <ChevronRight size={18} className="ds-stat-chevron" />
              </div>
              <div
                className="ds-stat-card ds-stat-card-clickable"
                onClick={() => setStatusModal("FAILED")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setStatusModal("FAILED")}
              >
                <div className="ds-stat-icon ds-stat-icon-draft">
                  <FileEdit size={20} />
                </div>
                <div className="ds-stat-content">
                  <span className="ds-stat-value">
                    {loadingStats ? "-" : animatedFailed}
                  </span>
                  <span className="ds-stat-label">Failed / partial</span>
                </div>
                <ChevronRight size={18} className="ds-stat-chevron" />
              </div>
            </div>
            <ContentStatusListModal
              isOpen={statusModal !== null}
              onClose={() => setStatusModal(null)}
              status={statusModal ?? "SCHEDULED"}
              title={
                statusModal === "DRAFT"
                  ? "In draft"
                  : statusModal === "PROCESSING"
                    ? "Processing"
                    : statusModal === "FAILED"
                      ? "Failed"
                      : statusModal === "PARTIAL_FAILURE"
                        ? "Partial failures"
                        : "Scheduled"
              }
              onViewItem={handleOpenLog}
            />

            <section className="ds-section" id="dashboard-brands">
              <div className="ds-section-head">
                <h2 className="ds-section-title">Your brands</h2>
                <button
                  type="button"
                  className="ds-btn-primary"
                  disabled={isAddBrandDisabled}
                  title={addBrandDisabledReason}
                  onClick={handleOpenAddBrandModal}
                >
                  <Plus size={18} />
                  Add brand
                </button>
              </div>
              <div className="ds-brand-grid">
                {loadingBrandList ? (
                  brandShimmer()
                ) : brandList && brandList.length > 0 ? (
                  brandList.map((brand: BrandDetails) => (
                    <BrandCard
                      key={brand.id}
                      label={brand.brand_name}
                      description={`Last active: ${new Date(
                        brand.created_at,
                      ).toLocaleDateString()}`}
                      toneOfVoice={getList(brand.marketing_strategy?.tone)}
                      target_audience={getList(
                        brand.marketing_strategy?.target_audience,
                      )}
                      socialMedias={brand.connected_platforms || []}
                      onDelete={() => onDeleteClick(brand)}
                      isDeleteVisible={true}
                      onViewDetails={() => onViewDetailsClick(brand)}
                      className="ds-brand-card"
                    />
                  ))
                ) : (
                  <div className="ds-empty-state">
                    <Building2 size={40} className="ds-empty-icon" />
                    <p className="ds-empty-title">No brands yet</p>
                    <p className="ds-empty-desc">
                      Create your first brand to start generating content.
                    </p>
                    <button
                      type="button"
                      className="ds-btn-primary"
                      disabled={isAddBrandDisabled}
                      title={addBrandDisabledReason}
                      onClick={handleOpenAddBrandModal}
                    >
                      <Plus size={18} />
                      Add brand
                    </button>
                  </div>
                )}
              </div>
            </section>

            <section className="ds-section" id="dashboard-weekly-work-queue">
              <div className="ds-section-head ds-section-head--stacked">
                <div>
                  <h2 className="ds-section-title">Weekly work queue</h2>
                  <p className="ds-section-subtitle">
                    Uses persisted weekly plans when available, with content history as the fallback.
                  </p>
                </div>
              </div>
              <WeeklyWorkQueue
                brandList={brandList}
                contentLogs={rawContentLogs}
                isLoading={loadingBrandList || loadingContentLogs}
                onOpenWeeklyPlan={(brand) =>
                  handleOpenBrandTab(brand, "weekly-plan")
                }
                onOpenCreator={(brand) => handleOpenBrandTab(brand, "drafts")}
                onOpenHistory={(brand) => handleOpenBrandTab(brand, "history")}
                onOpenCalendar={(brand) =>
                  handleOpenBrandTab(brand, "calendar")
                }
              />
            </section>

            <section className="ds-section" id="dashboard-generations">
              <div className="ds-section-head">
                <h2 className="ds-section-title">Recent generations</h2>
              </div>
              <div className="ds-card ds-table-card">
                {loadingContentLogs ? (
                  tableShimmer()
                ) : contentLogs && contentLogs.length > 0 ? (
                  <div className="ds-table-wrap">
                    <table className="ds-table">
                      <thead>
                        <tr>
                          <th>Brand</th>
                          <th>Topic</th>
                          <th>Platform</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {contentLogs.map((gen) => (
                          <tr key={gen.id}>
                            <td className="ds-cell-brand">{gen.brand_name}</td>
                            <td>{gen.topic}</td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                }}
                              >
                                {gen.content.platforms?.map((platform) => {
                                  const icon = getIconForPlatform(platform);
                                  return icon ? (
                                    <img
                                      key={platform}
                                      src={icon}
                                      alt={platform}
                                      style={{ width: "18px", height: "18px" }}
                                    />
                                  ) : null;
                                })}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`ds-pill ds-pill-st-${(
                                  gen.status || ""
                                )
                                  .toLowerCase()
                                  .replace(/\s+/g, "-")}`}
                              >
                                {gen.status}
                              </span>
                              {gen.content?.pfm_result?.failure_reason && (
                                <div
                                  style={{
                                    marginTop: "4px",
                                    color: "#b91c1c",
                                    fontSize: "12px",
                                    lineHeight: 1.4,
                                    maxWidth: "220px",
                                  }}
                                >
                                  {gen.content.pfm_result.failure_reason}
                                </div>
                              )}
                            </td>
                            <td className="ds-cell-muted">
                              {formatDate(gen.created_at, "MMM DD, YYYY HH:mm")}
                            </td>
                            <td>
                              <span
                                className="ds-link"
                                onClick={() => {
                                  const isPublished =
                                    (gen.status || "").toUpperCase() ===
                                    "PUBLISHED";
                                  const brand = brandList?.find(
                                    (b) => b.id === gen.product_id,
                                  );
                                  if (brand) {
                                    handleOpenLog(gen);
                                  } else {
                                    push(
                                      AppRoute.BRAND_DETAILS.replace(
                                        ":id",
                                        gen.product_id,
                                      ),
                                      isPublished
                                        ? { analyticsItem: gen }
                                        : { historyItem: gen },
                                    );
                                  }
                                }}
                              >
                                {(gen.status || "").toUpperCase() ===
                                "PUBLISHED"
                                  ? "Analytics"
                                  : "Edit"}{" "}
                                <ChevronRight size={14} />
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ds-empty-inline">
                    <FileText size={32} className="ds-empty-icon" />
                    <p>
                      No generations yet. Create content from a brand’s Creator
                      Studio.
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>

      <Modal
        isOpen={showAddBrandModal}
        onClose={() => setShowAddBrandModal(false)}
        title="Add brand"
        size="large"
        showCloseButton={true}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <AddBrand
          embedded
          onSuccess={handleAddBrandSuccess}
          onCancel={() => setShowAddBrandModal(false)}
        />
      </Modal>

      <Modal
        isOpen={deleteConfirmation.isOpen}
        onClose={onDeleteCancel}
        title="Delete brand"
        size="medium"
        showCloseButton={false}
        headerStyle={{ borderBottom: "none", paddingBottom: "12px" }}
        bodyStyle={{ paddingTop: "0px", paddingBottom: "12px" }}
        footerStyle={{ borderTop: "none", paddingTop: "0px" }}
        footer={
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <button
              type="button"
              onClick={onDeleteCancel}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#334155",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onDeleteConfirm}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#fff",
                background: "#dc2626",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        }
      >
        <p
          style={{
            color: "#64748b",
            fontSize: "14px",
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          Are you sure you want to delete the brand{" "}
          <strong style={{ color: "#0f172a" }}>
            "{deleteConfirmation.brand?.brand_name}"
          </strong>
          ? This action cannot be undone.
        </p>
      </Modal>
      {upgradeModal}
    </div>
  );
};

export default Dashboard;
